/**
 * MoveEntityCommand - Generic command for moving entities (StepNode, StrategyBlock)
 *
 * Implements the Command pattern for undoable entity movement.
 * Delegates to FlowCoordinator for actual position update logic.
 *
 * Generic Architecture:
 * - TEntity extends FlowEntity (StepNode, StrategyBlock)
 * - TService (StepNodeScreenService, StrategyBlockService)
 * - Uses ONLY generic coordinator methods (updateEntityPositions)
 * - Supports batch movement (multiple entities moved together)
 *
 * Usage:
 * ```typescript
 * // Move single V2 StepNode
 * const command = new MoveEntityCommand<FlowStepNode, StepNodeScreenService>(
 *   [{
 *     entityId: 'screen-1',
 *     oldPosition: { x: 0, y: 0 },
 *     newPosition: { x: 100, y: 200 }
 *   }],
 *   screenContext
 * );
 *
 * // Move multiple blocks
 * const command = new MoveEntityCommand<StrategyBlock, StrategyBlockService>(
 *   [
 *     { entityId: 'block-1', oldPosition: {x:0,y:0}, newPosition: {x:100,y:0} },
 *     { entityId: 'block-2', oldPosition: {x:0,y:100}, newPosition: {x:100,y:100} }
 *   ],
 *   blockContext
 * );
 *
 * await commandManager.execute(command);
 * ```
 *
 * Related Requirements:
 * - FR-P1-003: Commands use coordinator ONLY for all operations
 * - FR-P3-001: Generic FlowCoordinator interface
 * - FR-P3-004: Generic CommandContext
 */

import { Command, CommandResult } from './Command';
import { CommandContext } from './CommandContext';
import type { FlowEntity, EntityMove } from '@modules/reactFlow/types/flow-canvas-types';

/**
 * MoveEntityCommand - Generic command for moving flow entities
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export class MoveEntityCommand<
  TEntity extends FlowEntity,
  TService
> implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  /**
   * @param moves - Array of entity movements (entityId + old/new positions)
   * @param context - Command execution context
   */
  constructor(
    private moves: EntityMove[],
    private context: CommandContext<TEntity, TService>
  ) {
    this.id = `move-entity-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.timestamp = Date.now();

    // Description shows number of entities moved
    const entityCount = moves.length;
    this.description = entityCount === 1
      ? `Move entity to (${moves[0].newPosition.x}, ${moves[0].newPosition.y})`
      : `Move ${entityCount} entities`;
  }

  /**
   * Execute command - Move entities to new positions via coordinator
   *
   * Flow:
   * 1. Validate entities exist
   * 2. Call coordinator.updateEntityPositions() - handles Registry + Canvas + Firebase
   * 3. Return success result
   *
   * Note: Position updates are optimized (may not trigger full re-render)
   *
   * @returns Promise resolving with command result
   */
  async execute(): Promise<CommandResult> {
    if (!this.canExecute()) {
      return {
        success: false,
        error: new Error('Cannot execute: no moves provided or entities not found'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[MoveEntityCommand] 🔄 Execute:', {
        moveCount: this.moves.length,
        entityIds: this.moves.map(m => m.entityId)
      });

      // Show loading indicator
      this.context.setIsSyncing?.(true);
      this.context.setSyncOperation?.(`Moving ${this.moves.length} entity(ies)...`);

      // Coordinator handles EVERYTHING (Registry + Canvas + Firebase)
      // Uses unified interface method - no branching logic needed
      await this.context.coordinator.updateEntityPositions(this.moves);

      console.log('[MoveEntityCommand] ✅ Updated positions via Coordinator');

      // Hide loading indicator
      this.context.setIsSyncing?.(false);
      this.context.setSyncOperation?.('');

      return {
        success: true,
        affectedEntities: { screens: this.moves.map(m => m.entityId) }
      };
    } catch (error) {
      console.error('[MoveEntityCommand] ❌ Move failed:', error);
      this.context.setIsSyncing?.(false);
      this.context.setSyncOperation?.('');

      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Undo command - Restore old positions via coordinator
   *
   * Flow:
   * 1. Call coordinator.updateEntityPositions() with old positions
   * 2. Return success result
   *
   * @returns Promise resolving with command result
   */
  async undo(): Promise<CommandResult> {
    try {
      console.log('[MoveEntityCommand] ⏮️ Undo:', {
        moveCount: this.moves.length,
        entityIds: this.moves.map(m => m.entityId)
      });

      // Coordinator handles EVERYTHING (Registry + Canvas + Firebase)
      // Swap positions for undo - uses unified interface method
      const undoMoves = this.moves.map(m => ({
        entityId: m.entityId,
        oldPosition: m.newPosition, // Swapped for undo
        newPosition: m.oldPosition
      }));

      await this.context.coordinator.updateEntityPositions(undoMoves);

      console.log('[MoveEntityCommand] ✅ Undo successful');

      return {
        success: true,
        affectedEntities: { screens: this.moves.map(m => m.entityId) }
      };
    } catch (error) {
      console.error('[MoveEntityCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Check if command can be executed
   * @returns true if at least one move provided and all entities exist
   */
  canExecute(): boolean {
    if (this.moves.length === 0) {
      return false;
    }

    // Validate via Coordinator - all entities must exist
    const allEntities = this.context.coordinator.getAllEntities();
    return this.moves.every(move =>
      allEntities.find(e => e.entityId === move.entityId)
    );
  }

  /**
   * Check if command can be undone
   * @returns Always true (movement always reversible)
   */
  canUndo(): boolean {
    return true;
  }
}
